import React, { useState } from 'react';
import { IoCheckmark, IoCheckmarkDone, IoCamera, IoVideocam, IoDocument, IoMusicalNotes, IoTrashOutline, IoCloseCircleOutline } from 'react-icons/io5';
import { chatAPI } from './api';

const isOnline = (activeTime) => {
  if (!activeTime) return false;
  const utcTimestamp = activeTime.includes('Z') || activeTime.includes('+')
    ? activeTime
    : activeTime.replace(' ', 'T') + 'Z';
  const diffInSeconds = (new Date() - new Date(utcTimestamp)) / 1000;
  return diffInSeconds <= 90;
};

const ChatList = ({ chats, onChatSelect, selectedChat, currentUser, onRefreshChats, onRemoveChat }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [toastMessage, setToastMessage] = useState(null);

  const filteredChats = chats.filter(chat =>
    chat.user?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    chat.user?.last_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Function to render last message with media indicators
  const renderLastMessage = (chat) => {
    const lastMessage = chat.last_message;

    // Check if it's our special empty message indicator
    if (lastMessage === 'QmtsoYltwVw') {
      // Try to determine file type from chat data if available
      const fileType = chat.file_type?.toLowerCase();

      if (fileType) {
        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileType);
        const isVideo = ['mp4', 'mkv', 'avi', 'mov', 'wmv'].includes(fileType);
        const isAudio = ['mp3', 'wav', 'ogg', 'aac'].includes(fileType);

        if (isImage) {
          return (
            <span className="media-message">
              <IoCamera size={14} className="media-icon" />
              Photo
            </span>
          );
        } else if (isVideo) {
          return (
            <span className="media-message">
              <IoVideocam size={14} className="media-icon" />
              Video
            </span>
          );
        } else if (isAudio) {
          return (
            <span className="media-message">
              <IoMusicalNotes size={14} className="media-icon" />
              Audio
            </span>
          );
        } else {
          return (
            <span className="media-message">
              <IoDocument size={14} className="media-icon" />
              Document
            </span>
          );
        }
      } else {
        // Fallback when file type is not available
        return (
          <span className="media-message">
            <IoCamera size={14} className="media-icon" />
            Media
          </span>
        );
      }
    }

    // Return normal message or fallback
    return lastMessage || 'No messages yet';
  };

  const showToast = (message, type = 'success') => {
    setToastMessage({ message, type });
    setTimeout(() => setToastMessage(null), 3000);
  };

  const handleDeleteAllMessages = async (e, chat) => {
    e.stopPropagation(); // Prevent chat selection when clicking delete

    const userName = `${chat.user?.first_name || ''} ${chat.user?.last_name || ''}`.trim() || 'Unknown User';
    if (!window.confirm(`Are you sure you want to delete all messages with ${userName}? This action cannot be undone.`)) {
      return;
    }

    try {
      await chatAPI.deleteAllMessages(chat.chat_id, currentUser);
      showToast('All messages deleted successfully');
      // Refresh chat list to update the UI
      if (onRefreshChats) {
        onRefreshChats();
      }
    } catch (error) {
      console.error('Error deleting all messages:', error);
      showToast('Failed to delete messages. Please try again.', 'error');
    }
  };

  const handleRemoveChat = async (e, chat) => {
    e.stopPropagation();
    const userName = `${chat.user?.first_name || ''} ${chat.user?.last_name || ''}`.trim() || 'Unknown User';
    if (!window.confirm(`Remove chat with ${userName}? This conversation will be permanently deleted.`)) {
      return;
    }
    try {
      await chatAPI.deleteChat(chat.chat_id, currentUser);
      showToast('Chat removed successfully');
      if (onRemoveChat) {
        onRemoveChat(chat.chat_id);
      }
    } catch (error) {
      console.error('Error removing chat:', error);
      showToast('Failed to remove chat. Please try again.', 'error');
    }
  };

  return (
    <div className="chat-list">
      <div className="chat-list-header">
        <h2>Messages</h2>
      </div>
      
      <div className="search-container">
        <input
          type="text"
          placeholder="Search conversations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="chat-items">
        {filteredChats.length > 0 ? (
          filteredChats.map((chat) => (
            <div
              key={chat.chat_id}
              className={`chat-item ${selectedChat?.chat_id === chat.chat_id ? 'active' : ''}`}
              onClick={() => onChatSelect(chat)}
            >
              <div className="chat-avatar-wrapper">
                <div className="chat-avatar">
                  {chat.user?.photo ? (
                    <img
                      src={`https://buzzinguniverse.com/backend/${chat.user.photo}`}
                      alt="Avatar"
                      className="avatar-img"
                    />
                  ) : (
                    chat.user?.first_name?.charAt(0) || 'U'
                  )}
                </div>
                <span className={`chat-status-dot ${isOnline(chat.user?.active_time) ? 'dot-online' : 'dot-offline'}`}></span>
              </div>
              <div className="chat-info">
                <div className="chat-name">{`${chat.user?.first_name || ''} ${chat.user?.last_name || ''}`.trim() || 'Unknown User'}</div>
                <div className="chat-last-message">
                  {parseInt(chat?.sender?.id) === currentUser && (
                    <span className="message-status">
                      {chat.seen === '1' ? (
                        <IoCheckmarkDone size={12} />
                      ) : (
                        <IoCheckmark size={12} />
                      )}
                    </span>
                  )}
                  {renderLastMessage(chat)}
                </div>
              </div>
              <div className="chat-meta">
                <div className="chat-time">
                  {chat.last_time ? new Date(chat.last_time).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute:'2-digit'
                  }) : ''}
                </div>
                {chat.unread_count > 0 && (
                  <div className="unread-badge">{chat.unread_count}</div>
                )}
              </div>
              <div className="chat-action-btns">
                <button
                  className="delete-chat-btn"
                  onClick={(e) => handleDeleteAllMessages(e, chat)}
                  title="Delete all messages"
                >
                  <IoTrashOutline size={16} />
                </button>
                <button
                  className="remove-chat-btn"
                  onClick={(e) => handleRemoveChat(e, chat)}
                  title="Remove chat"
                >
                  <IoCloseCircleOutline size={16} />
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="no-chats">
            <p>No conversations found</p>
          </div>
        )}
      </div>

      {/* Toast Notification */}
      {toastMessage && (
        <div className={`toast-notification-chatlist ${toastMessage.type}`}>
          {toastMessage.message}
        </div>
      )}
    </div>
  );
};

export default ChatList;